import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { ArrowRight, CalendarDays, Check, ChevronLeft, ChevronRight, Gift, Minus, Sparkles, Youtube } from "lucide-react";
import { SKIP } from "@/lib/schedule";
import {
  ANNUAL, Btn, Card, Chip, DENVER, Hand, LocalTime, P, PLANS, PLink, PortalShell, SESSIONS, Section, Segmented,
  can, currentProject, denverDay, fmtDay, sessionsFor, liveSession, projectOf, sessionState, upcomingSessions, usePortal,
  type Plan, type Role, type Session, type Thing,
} from "./Portal";
import "./Join.css";

/* /blue-2/portal/join — the tier lens (research §5, P4).

   Every lock in the portal links here as join?plan=<free|friend|member>; the
   named plan is highlighted and the header says what it unlocks. Plans are a
   checkmark comparison (no prose benefits), with a monthly/annual toggle and
   the 7-day trial note. The schedule is a real month calendar plus the next
   three sessions in the viewer's time. Then the "replay-only?" answer and a
   moving-from-Patreon section. Buttons only show a toast: nothing is charged. */

/* ─────────── Toast (shared by Join, Me and Community; not in the kit yet) ─────────── */

/** A small transient message for prototype-only actions. `const [toast, show] = useToast();` render `{toast}` anywhere. */
export function useToast() {
  const [msg, setMsg] = useState<{ text: ReactNode; id: number } | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = window.setTimeout(() => setMsg(null), 4200);
    return () => window.clearTimeout(t);
  }, [msg]);
  const show = useCallback((text: ReactNode) => setMsg({ text, id: Date.now() }), []);
  const node = <div className="ptj-toast-wrap" aria-live="polite" role="status">
    {msg && <div key={msg.id} className="ptj-toast"><Check size={16} aria-hidden="true" /><span>{msg.text}</span></div>}
  </div>;
  return [node, show] as const;
}

/* ─────────── Plans ─────────── */

const ORDER: Plan[] = ["free", "friend", "member"];
const isPlan = (s: string | null): s is Plan => !!s && (ORDER as string[]).includes(s);
const rank = (r: Role) => (r === "visitor" ? -1 : r === "chelsea" ? 2 : ORDER.indexOf(r as Plan));

/** Comparison rows. Ticks come from can(), so they always match the locks. */
const ROWS: { group: string; items: { thing: Thing; label: string; note?: string }[] }[] = [
  { group: "Classes", items: [
    { thing: "live", label: "Live classes with Chelsea", note: "2 a month, 7 PM MT" },
    { thing: "replays", label: "Every replay", note: "Chapters and fix notes" },
    { thing: "chatReplay", label: "Live chat replay" },
    { thing: "qaAsk", label: "Ask Chelsea about any class" },
  ] },
  { group: "Projects", items: [
    { thing: "code", label: "Pattern discount codes" },
    { thing: "supplies", label: "Supply lists you can tick off" },
    { thing: "calendarFeed", label: "Calendar feed", note: "Updates when plans change" },
  ] },
  { group: "Community", items: [
    { thing: "notes", label: "Notes from Chelsea" },
    { thing: "communityRead", label: "Read makes and Q&A" },
    { thing: "makesPost", label: "Share makes and post" },
    { thing: "posts", label: "Studio posts and behind the scenes" },
  ] },
  { group: "Free for everyone", items: [
    { thing: "youtube", label: "YouTube lives", note: "Most other Tuesdays" },
  ] },
];

const UNLOCKS: Record<Plan, string> = {
  free: "A free account gets you notes from Chelsea, supply lists and the community to read.",
  friend: "Thread Friends read the studio posts, post in the community and share their makes.",
  member: "Members sew along live, get every replay with chapters, the pattern codes, and can ask Chelsea anything.",
};

function price(plan: Plan, annual: boolean) {
  if (plan === "free") return { big: "$0", per: "", sub: "No card needed" };
  const yr = ANNUAL[plan], mo = PLANS[plan].price;
  return annual
    ? { big: `$${yr}`, per: "/yr", sub: `$${(yr / 12).toFixed(2)}/mo · 2 months free` }
    : { big: `$${mo}`, per: "/mo", sub: `or $${yr}/yr (save 17%)` };
}

export function PortalJoin() {
  const { role, now, tz } = usePortal();
  const [sp] = useSearchParams();
  const asked: Plan = isPlan(sp.get("plan")) ? (sp.get("plan") as Plan) : "member";
  // A plan the viewer already has isn't something to "unlock": point at Member instead.
  const want: Plan = rank(role) >= ORDER.indexOf(asked) ? "member" : asked;
  const [annual, setAnnual] = useState(false);
  const [toast, show] = useToast();
  const mine = rank(role);

  useEffect(() => {
    // #faq / #patreon / #schedule deep links (react-router doesn't scroll to hashes)
    const h = window.location.hash.slice(1);
    if (h) window.setTimeout(() => document.getElementById(h)?.scrollIntoView({ block: "start" }), 80);
  }, []);
  useEffect(() => {
    // Arriving from a lock: bring the named plan into view on phones.
    if (!sp.get("plan") || window.location.hash || window.innerWidth >= 900) return;
    const el = document.getElementById(`plan-${want}`);
    if (el) window.setTimeout(() => el.scrollIntoView({ block: "center" }), 60);
  }, [sp, want]);

  const checkout = (plan: Plan) => {
    if (plan === "free") return show("Prototype: this would create your free account (email link, no password).");
    const p = PLANS[plan];
    const bill = annual ? `annual ($${ANNUAL[plan]}/yr)` : `monthly (${p.per})`;
    const trial = plan === "member" && role !== "friend" ? " with a 7-day free trial" : role === "friend" ? ", prorated from today" : "";
    show(`Prototype: this would open checkout for ${p.name} · ${bill}${trial}. Nothing is charged here.`);
  };

  const headline = role === "member" || role === "chelsea"
    ? { k: "Your membership", t: "You have everything", s: "You're on the Member plan. Here's what each plan includes, for when friends ask." }
    : role === "friend"
      ? { k: `You're a ${PLANS.friend.tag}`, t: "Sew along live with Chelsea", s: "Upgrade to Member and keep everything you have now. You pay the difference for this month." }
      : { k: `Unlocks with ${want === "free" ? "a free account" : PLANS[want].tag}`, t: want === "member" ? "Sew along with Chelsea" : want === "friend" ? "Become a Thread Friend" : "Make a free account", s: UNLOCKS[want] };

  return <PortalShell active={null} kicker={headline.k} title={headline.t} subtitle={headline.s} back={{ to: P.home, label: "Club home" }}>
    {toast}

    {/* Plans */}
    <section className="ptj-plans-wrap" aria-labelledby="ptj-plans-h">
      <div className="ptj-plans-head">
        <h2 id="ptj-plans-h" className="pt-sechead-title">{role === "member" || role === "chelsea" ? "Plans" : "Choose a plan"}</h2>
        <div className="ptj-bill">
          <Segmented size="sm" label="Billing" value={annual ? "yr" : "mo"} onChange={v => setAnnual(v === "yr")}
            options={[{ value: "mo", label: "Monthly" }, { value: "yr", label: "Annual" }]} />
          <Chip tone="gold">Save 17%</Chip>
        </div>
      </div>
      <div className="ptj-plans">
        {ORDER.map(plan => {
          const p = PLANS[plan], pr = price(plan, annual);
          const isMine = (role === "chelsea" ? "member" : role) === plan;
          const included = mine > ORDER.indexOf(plan);
          const hl = plan === want && !isMine && !included;
          return <article key={plan} id={`plan-${plan}`} className={`ptj-plan ptj-plan-${plan}${hl ? " is-want" : ""}${isMine ? " is-mine" : ""}`}>
            {plan === "member" && <span className="ptj-plan-flag">Most popular</span>}
            <header>
              <p className="ptj-plan-name">{p.name}</p>
              <p className="ptj-plan-price"><b>{pr.big}</b>{pr.per && <span>{pr.per}</span>}</p>
              <p className="ptj-plan-sub">{pr.sub}</p>
            </header>
            <ul className="ptj-plan-perks">
              {p.perks.map(x => <li key={x}><Check size={16} aria-hidden="true" />{x}</li>)}
            </ul>
            <div className="ptj-plan-cta">
              {isMine ? <Btn variant="secondary" block to={P.me}>Your plan · Manage</Btn>
                : included ? <p className="ptj-plan-incl"><Check size={15} aria-hidden="true" /> Included in your plan</p>
                : <Btn variant={plan === want ? "primary" : "secondary"} size="lg" block onClick={() => checkout(plan)}>
                    {plan === "free" ? "Create free account" : plan === "friend" ? "Become a Thread Friend" : role === "friend" ? "Upgrade to Member" : "Start 7-day free trial"}
                  </Btn>}
              {plan === "member" && !isMine && !included && <p className="ptj-plan-note">{role === "friend" ? "Your Thread Friend perks stay. Cancel anytime." : `7 days free, then ${annual ? `$${ANNUAL.member}/yr` : "$25/mo"}. Cancel anytime before and you pay nothing.`}</p>}
            </div>
          </article>;
        })}
      </div>
      {role === "visitor" && <p className="ptj-signin">Already a member? <PLink to={P.me}>Sign in</PLink></p>}
    </section>

    <Compare want={want} role={role} />

    <Schedule now={now} tz={tz} role={role} />

    <Faq />

    <Patreon onCheck={() => show("Prototype: we'd look up that email in the Patreon export and carry over your badge and member-since date.")} />

    {!(role === "member" || role === "chelsea") && <FinalCta now={now} cta={role === "friend" ? "Upgrade to Member" : "Start 7-day free trial"} onJoin={() => checkout("member")} />}
  </PortalShell>;
}

function FinalCta({ now, cta, onJoin }: { now: Date; cta: string; onJoin: () => void }) {
  const proj = currentProject(now);
  const classes = sessionsFor(proj.id).filter(s => !s.recorded);
  return <Card tone="teal" pad="lg" tape="pink" className="ptj-final">
    <Hand>pull up a chair!</Hand>
    <h2 className="ptj-final-t">{proj.title} is this month's project</h2>
    <ul className="ptj-final-list">
      {classes.map(s => <li key={s.id}><b>{s.label}</b> <LocalTime date={s.start} withDay /></li>)}
    </ul>
    <p>Can't make it live? The replay is up the next morning.</p>
    <div className="ptj-final-btns">
      <Btn variant="pop" size="lg" onClick={onJoin}>{cta}</Btn>
      <Btn variant="secondary" onDark to={P.project(proj.id)}>See the project</Btn>
    </div>
  </Card>;
}

/* ─────────── Comparison ─────────── */

function Compare({ want, role }: { want: Plan; role: Role }) {
  const cur = role === "chelsea" ? "member" : role === "visitor" ? null : role;
  return <Section title="What each plan includes" kicker="Compare" id="compare">
    <div className="ptj-cmp" role="table" aria-label="Plan comparison">
      <div className="ptj-cmp-row ptj-cmp-head" role="row">
        <span role="columnheader" className="ptj-cmp-f"><span className="ptj-sr">Feature</span></span>
        {ORDER.map(p => <span key={p} role="columnheader" className={`ptj-cmp-c${p === want ? " is-want" : ""}`}>
          <b>{p === "friend" ? "Friend" : PLANS[p].name}</b><small>{p === "free" ? "$0" : PLANS[p].per}</small>
          {cur === p && <em>You</em>}
        </span>)}
      </div>
      {ROWS.map(g => <div key={g.group} role="rowgroup">
        <div className="ptj-cmp-group" role="row"><span role="cell">{g.group}</span></div>
        {g.items.map(it => <div key={it.thing} className="ptj-cmp-row" role="row">
          <span role="rowheader" className="ptj-cmp-f">{it.label}{it.note && <small>{it.note}</small>}</span>
          {ORDER.map(p => {
            const yes = can(p as Role, it.thing);
            return <span key={p} role="cell" className={`ptj-cmp-c${p === want ? " is-want" : ""}`}>
              {yes ? <span className="ptj-yes"><Check size={16} strokeWidth={3} aria-label="Included" /></span>
                : <Minus size={16} className="ptj-no" aria-label="Not included" />}
            </span>;
          })}
        </div>)}
      </div>)}
    </div>
  </Section>;
}

/* ─────────── Schedule: month calendar + next three ─────────── */

const pad = (n: number) => String(n).padStart(2, "0");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function Schedule({ now, tz, role }: { now: Date; tz: string; role: Role }) {
  const today = denverDay(now);
  const [ym, setYm] = useState(() => ({ y: +today.slice(0, 4), m: +today.slice(5, 7) - 1 }));
  const [pick, setPick] = useState<string | null>(null);
  const lives = useMemo(() => SESSIONS.filter(s => !s.recorded), []);
  const byDay = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const s of lives) { const k = denverDay(s.start); map.set(k, [...(map.get(k) ?? []), s]); }
    return map;
  }, [lives]);
  const first = new Date(Date.UTC(ym.y, ym.m, 1)).getUTCDay();
  const days = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const step = (d: number) => { setPick(null); setYm(v => { const m = v.m + d; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; }); };
  const minYm = 9, maxYm = 10; // Oct–Nov 2026 have sample data
  const live = liveSession(now);
  const next = [...(live ? [live] : []), ...upcomingSessions(now, 3)].slice(0, 3);
  const picked = pick ? byDay.get(pick) ?? [] : [];
  const member = can(role, "live");

  return <Section title="When we sew" kicker="Schedule" id="schedule"
    action={<PLink to={P.me} className="pt-sechead-action">Times in {tz === DENVER ? "MT" : "your time"}<ChevronRight size={16} aria-hidden="true" /></PLink>}>
    <div className="ptj-sched">
      <Card pad="md" className="ptj-cal">
        <div className="ptj-cal-head">
          <button type="button" className="ptj-cal-nav" onClick={() => step(-1)} disabled={ym.m <= minYm} aria-label="Previous month"><ChevronLeft size={18} /></button>
          <p className="ptj-cal-month">{MONTHS[ym.m]} {ym.y}</p>
          <button type="button" className="ptj-cal-nav" onClick={() => step(1)} disabled={ym.m >= maxYm} aria-label="Next month"><ChevronRight size={18} /></button>
        </div>
        <div className="ptj-cal-grid" role="grid" aria-label={`${MONTHS[ym.m]} ${ym.y}`}>
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} className="ptj-cal-dow" role="columnheader" aria-hidden="true">{d}</span>)}
          {cells.map((d, i) => {
            if (!d) return <span key={i} className="ptj-cal-cell is-empty" />;
            const k = `${ym.y}-${pad(ym.m + 1)}-${pad(d)}`;
            const ss = byDay.get(k) ?? [];
            const club = ss.find(s => s.kind === "club"), yt = ss.find(s => s.kind === "youtube");
            const skip = SKIP[k];
            const label = [fmtDay(new Date(Date.UTC(ym.y, ym.m, d, 18)), DENVER, { weekday: "long", month: "long", day: "numeric" }),
              ...ss.map(s => s.title), skip ? `No live: ${skip}` : ""].filter(Boolean).join(". ");
            const tag = club ? <span className="ptj-cal-tag is-club">{club.label.replace("Class ", "C")}</span>
              : yt ? <span className="ptj-cal-tag is-yt">YT</span> : skip ? <span className="ptj-cal-tag is-skip">Off</span> : null;
            const cls = `ptj-cal-cell${k === today ? " is-today" : ""}${k < today ? " is-past" : ""}${club ? " has-club" : yt ? " has-yt" : ""}${pick === k ? " is-pick" : ""}`;
            return ss.length || skip
              ? <button key={i} type="button" className={cls} aria-label={label} aria-pressed={pick === k} onClick={() => setPick(p => (p === k ? null : k))}><b>{d}</b>{tag}</button>
              : <span key={i} className={cls} aria-label={label}><b>{d}</b></span>;
          })}
        </div>
        <div className="ptj-cal-legend">
          <span><i className="is-club" />Club class · Members</span>
          <span><i className="is-yt" />YouTube live · free</span>
          <span><i className="is-today" />Today</span>
        </div>
        {pick && <div className="ptj-cal-pick">
          {picked.length ? picked.map(s => <SessionLine key={s.id} s={s} now={now} member={member} />)
            : <p className="ptj-cal-skip"><b>No live that night.</b> {SKIP[pick]}.</p>}
        </div>}
      </Card>

      <div className="ptj-next">
        <h3 className="ptj-next-h">Next three</h3>
        <ul className="ptj-next-list">
          {next.map(s => <li key={s.id}><SessionLine s={s} now={now} member={member} big /></li>)}
        </ul>
        <p className="ptj-next-note"><CalendarDays size={16} aria-hidden="true" />Club classes are the 2nd Tuesday and the Thursday after, 7 PM MT, 2–3 hours. Most other Tuesdays there's a free YouTube live.</p>
      </div>
    </div>
  </Section>;
}

function SessionLine({ s, now, member, big }: { s: Session; now: Date; member: boolean; big?: boolean }) {
  const { tz } = usePortal();
  const st = sessionState(s, now);
  const proj = projectOf(s);
  const d = new Date(s.start);
  return <div className={`ptj-sl${big ? " is-big" : ""}`}>
    <span className={`ptj-sl-date${s.kind === "youtube" ? " is-yt" : ""}`} aria-hidden="true">
      <small>{fmtDay(d, tz, { weekday: "short" })}</small><b>{fmtDay(d, tz, { day: "numeric" })}</b>
    </span>
    <span className="ptj-sl-main">
      <span className="ptj-sl-t">{s.kind === "club" ? `${proj?.title ?? ""} · ${s.label}` : s.title}</span>
      <LocalTime date={s.start} relative className="ptj-sl-m" />
    </span>
    <span className="ptj-sl-trail">
      {st === "live" ? <Chip tone="live">Live</Chip>
        : s.kind === "youtube" ? <Chip tone="outline" icon={<Youtube size={13} aria-hidden="true" />}>Free</Chip>
        : member ? <Chip tone="teal">Members</Chip> : <Chip tone="lock">Member ($25)</Chip>}
    </span>
  </div>;
}

/* ─────────── FAQ ─────────── */

const FAQ: { id: string; q: string; a: ReactNode }[] = [
  { id: "replay-only", q: "Can't make 7 PM MT. Is there a replay-only plan?",
    a: <>Not a separate one, but most members watch the replays, and that's fine. Every class is up by the next morning with chapters, fix notes and the live chat, so you can sew along on your own schedule. Replays are part of the Member plan. If a cheaper replay-only plan would suit you, tell Chelsea in #general; she's thinking about it.</> },
  { id: "trial", q: "How does the 7-day free trial work?",
    a: <>You get the whole Member plan for 7 days, including any live class in that week and every replay. Cancel from Me before day 7 and you're not charged.</> },
  { id: "friend", q: "What's a Thread Friend?",
    a: <>A $3/mo way to support Chelsea. You get the studio posts, can post in the community and share your makes. Live classes and replays are on the Member plan.</> },
  { id: "tz", q: "I'm not on Mountain time.",
    a: <>Every time in the club is shown in your own time zone next to Mountain time, and the calendar feed adds classes to your calendar with a 15-minute reminder.</> },
  { id: "cancel", q: "Can I cancel or switch plans?",
    a: <>Anytime, from Me. Annual plans are refunded for the months you haven't used. Switching up takes effect right away.</> },
  { id: "gift", q: "Can I give a membership as a gift?",
    a: <>Yes: 1, 3 or 12 months, from Me → Gift a membership. You pick the send date.</> },
];

function Faq() {
  const [open, setOpen] = useState<string>("replay-only");
  return <Section title="Questions" kicker="FAQ" id="faq">
    <div className="ptj-faq">
      {FAQ.map(f => <div key={f.id} className={`ptj-faq-item${open === f.id ? " is-open" : ""}`} id={`faq-${f.id}`}>
        <h3><button type="button" aria-expanded={open === f.id} aria-controls={`faq-a-${f.id}`} onClick={() => setOpen(o => (o === f.id ? "" : f.id))}>
          <span>{f.q}</span><ChevronRight size={18} aria-hidden="true" />
        </button></h3>
        {open === f.id && <div className="ptj-faq-a" id={`faq-a-${f.id}`}><p>{f.a}</p></div>}
      </div>)}
    </div>
  </Section>;
}

/* ─────────── Moving from Patreon ─────────── */

function Patreon({ onCheck }: { onCheck: () => void }) {
  return <section className="ptj-pat" id="patreon" aria-labelledby="ptj-pat-h">
    <div className="ptj-pat-intro">
      <p className="pt-kicker">Moving from Patreon</p>
      <h2 id="ptj-pat-h" className="ptj-pat-t">Already a patron? Here's how to switch</h2>
      <p>The club has moved to its own home. Your history comes with you; there's no need to start over.</p>
      <Hand>same club, nicer house</Hand>
    </div>
    <ol className="ptj-pat-steps">
      <li><span className="ptj-pat-n">1</span><div><b>Join here with your Patreon email</b><p>Use the same email and we'll match you up.</p></div></li>
      <li><span className="ptj-pat-n">2</span><div><b>Your badge and history carry over</b><p>Founding members keep the Founding badge and their member-since date. Every replay from June on is already here, now with chapters.</p></div></li>
      <li><span className="ptj-pat-n">3</span><div><b>Then cancel on Patreon</b><p>Do it before your next Patreon billing date so you're not charged twice. The 7-day free trial covers the overlap.</p></div></li>
    </ol>
    <div className="ptj-pat-act">
      <Btn variant="secondary" icon={<Sparkles size={16} aria-hidden="true" />} onClick={onCheck}>Check my Patreon email</Btn>
      <PLink to={`${P.me}#gift`} className="ptj-pat-gift"><Gift size={16} aria-hidden="true" />Gifting instead?<ArrowRight size={15} aria-hidden="true" /></PLink>
    </div>
  </section>;
}
